import type { ComponentFileSelectionView } from './models/account-library';
import type { MediaGenerationView, MediaModelView } from './models/creative';

export interface ComponentImageGenerationInput {
  prompt: string;
  title?: string;
  parameters?: Record<string, unknown>;
  idempotencyKey: string;
}

export type ComponentMediaGenerationView = Pick<
  MediaGenerationView,
  | 'id'
  | 'status'
  | 'title'
  | 'prompt'
  | 'model'
  | 'credentialSource'
  | 'progress'
  | 'estimatedCredits'
  | 'chargedCredits'
  | 'error'
  | 'createdAt'
  | 'updatedAt'
> & { files: ComponentFileSelectionView[] };

/** 在 iframe 中序列化执行，所有权限和费用确认仍由宿主处理。 */
export function createComponentMediaApi(
  call: (method: string, payload: Record<string, unknown>) => Promise<unknown>,
) {
  const runtime = globalThis as unknown as {
    parent: unknown;
    console: { error(error: unknown): void };
    addEventListener(
      name: string,
      handler: (event: {
        source: unknown;
        data?: {
          channel?: string;
          generationId: string;
          data?: ComponentMediaGenerationView;
          error?: { message: string; code?: string };
          stopped?: boolean;
        };
      }) => void,
    ): void;
  };
  type Listener = {
    handler: (task: ComponentMediaGenerationView) => void | Promise<void>;
    onError?: (error: { message: string; code?: string }) => void;
    pending: Promise<void>;
    updatedAt: string;
    active: boolean;
  };
  const listeners = new Map<string, Set<Listener>>();
  let listening = false;
  function deliver(listener: Listener, task: ComponentMediaGenerationView) {
    if (task.updatedAt && task.updatedAt < listener.updatedAt) return listener.pending;
    listener.updatedAt = task.updatedAt ?? '';
    listener.pending = listener.pending
      .then(() => {
        if (listener.active) return listener.handler(task);
      })
      .catch((error) => {
        if (listener.onError) listener.onError(error);
        else runtime.console.error(error);
      })
      .catch((error) => runtime.console.error(error));
    return listener.pending;
  }
  async function subscribeGeneration(
    generationId: string,
    handler: Listener['handler'],
    onError?: Listener['onError'],
  ) {
    if (typeof generationId !== 'string' || !generationId || typeof handler !== 'function')
      throw new Error('请提供任务 ID 和图片生成处理器');
    if (!listening) {
      listening = true;
      runtime.addEventListener('message', (event) => {
        if (event.source !== runtime.parent || event.data?.channel !== 'component-media-generation')
          return;
        const message = event.data;
        const handlers = listeners.get(message.generationId);
        for (const listener of handlers ?? []) {
          if (message.error) {
            const error = message.error;
            listener.pending = listener.pending
              .then(() => {
                if (listener.onError) listener.onError(error);
                else runtime.console.error(error.message);
              })
              .catch((error) => runtime.console.error(error));
          } else if (message.data?.id === message.generationId)
            void deliver(listener, message.data);
        }
        if (message.stopped) {
          for (const listener of handlers ?? []) listener.active = false;
          listeners.delete(message.generationId);
        }
      });
    }
    const handlers = listeners.get(generationId) ?? new Set<Listener>();
    const listener: Listener = {
      handler,
      onError,
      pending: Promise.resolve(),
      updatedAt: '',
      active: true,
    };
    handlers.add(listener);
    listeners.set(generationId, handlers);
    try {
      const task = (await call('media.subscribeGeneration', {
        generationId,
      })) as ComponentMediaGenerationView;
      await deliver(listener, task);
    } catch (error) {
      listener.active = false;
      handlers.delete(listener);
      if (!handlers.size && listeners.get(generationId) === handlers)
        listeners.delete(generationId);
      throw error;
    }
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      const subscribed = listener.active;
      listener.active = false;
      handlers.delete(listener);
      if (!handlers.size && listeners.get(generationId) === handlers)
        listeners.delete(generationId);
      if (subscribed)
        void call('media.unsubscribeGeneration', { generationId }).catch(() => undefined);
    };
  }
  return {
    subscribeGeneration,
    getImageModel: () => call('media.getImageModel', {}) as Promise<MediaModelView>,
    generateImage: (input: ComponentImageGenerationInput) =>
      call('media.generateImage', { ...input }) as Promise<ComponentMediaGenerationView>,
    getGeneration: (generationId: string) =>
      call('media.getGeneration', { generationId }) as Promise<ComponentMediaGenerationView>,
    listGenerations: () =>
      call('media.listGenerations', {}) as Promise<ComponentMediaGenerationView[]>,
    cancelGeneration: (generationId: string) =>
      call('media.cancelGeneration', { generationId }) as Promise<ComponentMediaGenerationView>,
  };
}
